/**
 * Rolling estimates used to choose between "download the stored socket" and
 * "re-run the recipe".
 *
 * Both sides of that comparison are unknowable in advance, so both are measured:
 *
 *   download rate — sampled from real before-mesh and checkpoint downloads.
 *   device factor — how fast *this* machine runs tool pipelines relative to the
 *                   machine that recorded the timings in the manifest. Recorded
 *                   apply durations are meaningless across devices without it:
 *                   the same flare sweep is seconds apart on a workstation and a
 *                   laptop.
 *
 * Deliberately conservative defaults. A wrong guess must only cost time, never
 * correctness, and the caller aborts to the download path if a replay overruns
 * its budget — so when in doubt this leans towards downloading.
 */

/** Assumed throughput before any sample exists (~3 MB/s ≈ a modest clinic link). */
const DEFAULT_BYTES_PER_MS = 3 * 1024;

/** Weight of each new sample in the moving average. */
const SMOOTHING = 0.3;

let bytesPerMs = null;

/** Vertices/ms this device sustains on a trivial geometry sweep; null until measured. */
let deviceVerticesPerMs = null;

/**
 * Feed a completed transfer.
 * @param {number} bytes
 * @param {number} ms
 */
export function recordTransferSample(bytes, ms) {
  if (!Number.isFinite(bytes) || !Number.isFinite(ms) || ms <= 0 || bytes <= 0) return;
  const sample = bytes / ms;
  bytesPerMs = bytesPerMs == null ? sample : bytesPerMs * (1 - SMOOTHING) + sample * SMOOTHING;
}

/** @returns {number} bytes per millisecond */
export function currentBytesPerMs() {
  return bytesPerMs ?? DEFAULT_BYTES_PER_MS;
}

/**
 * Predicted milliseconds to fetch `bytes`. Returns 0 when the payload is already
 * on this device — the case where replay genuinely cannot win.
 *
 * @param {number} bytes
 * @param {{ alreadyLocal?: boolean }} [opts]
 */
export function predictDownloadMs(bytes, { alreadyLocal = false } = {}) {
  if (alreadyLocal) return 0;
  if (!Number.isFinite(bytes) || bytes <= 0) return Infinity;
  return bytes / currentBytesPerMs();
}

/**
 * Calibrate this device with a cheap arithmetic sweep over a typed array. Not a
 * benchmark of the tool pipelines themselves — just enough of a scalar to stop us
 * trusting another machine's milliseconds outright.
 *
 * Runs once; repeat calls reuse the first result.
 *
 * @returns {number} vertices per millisecond
 */
export function calibrateDevice() {
  if (deviceVerticesPerMs != null) return deviceVerticesPerMs;

  const N = 300000;
  const buf = new Float32Array(N * 3);
  for (let i = 0; i < buf.length; i++) buf[i] = i % 97;

  const started = performance.now();
  let acc = 0;
  for (let i = 0; i < buf.length; i += 3) {
    acc += Math.sqrt(buf[i] * buf[i] + buf[i + 1] * buf[i + 1] + buf[i + 2] * buf[i + 2]);
  }
  const elapsed = Math.max(performance.now() - started, 0.001);
  // Touch acc so the loop cannot be optimised away.
  if (!Number.isFinite(acc)) return 1;

  deviceVerticesPerMs = N / elapsed;
  return deviceVerticesPerMs;
}

/**
 * Reference throughput the recorded timings are normalised against. Set from the
 * same calibration on the machine that wrote them, so a manifest carries its own
 * yardstick; falls back to this build's constant for older manifests.
 */
export const REFERENCE_VERTICES_PER_MS = 8000;

/**
 * Scale a duration recorded elsewhere onto this device.
 *
 * @param {number} recordedMs
 * @param {number} [recordedDeviceVerticesPerMs] from the manifest, when present
 * @returns {number}
 */
export function scaleRecordedDuration(recordedMs, recordedDeviceVerticesPerMs) {
  if (!Number.isFinite(recordedMs) || recordedMs <= 0) return 0;
  const here = calibrateDevice();
  const there = Number.isFinite(recordedDeviceVerticesPerMs)
    ? recordedDeviceVerticesPerMs
    : REFERENCE_VERTICES_PER_MS;
  if (!here || !there) return recordedMs;
  return recordedMs * (there / here);
}

/**
 * How far a replay may overrun its prediction before we abandon it and download
 * instead. Generous, because aborting mid-replay wastes the work already done.
 */
export const REPLAY_OVERRUN_FACTOR = 3;

/** Diagnostics snapshot. */
export function transferRateDebug() {
  return {
    bytesPerMs: currentBytesPerMs(),
    sampled: bytesPerMs != null,
    deviceVerticesPerMs,
  };
}
